import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { useSafeSearchParams } from "@App/hooks/reactRouter/useSafeSearchParams";
import { State } from "@App/state";
import { setCallbackRef } from "@App/state/reducers/callbackRef";

const SESSION_STORAGE_KEY = "callbackRef";

/**
 * Reads the partner callback token from the `ref` query parameter, validates it
 * (32 alphanumeric characters) and persists it to the store and sessionStorage so
 * it stays available across the booking flow. A new valid token overwrites the
 * stored one; if no valid token is in the URL, the store is hydrated from
 * sessionStorage (e.g. after a full reload).
 *
 * Currently used by the FINN whitelabel only.
 */
export const useCallbackRefParameter = () => {
  const dispatch = useDispatch();
  const storedToken = useSelector((state: State) => state.callbackRef.token);

  const safeSearchParams = useSafeSearchParams(
    z.object({
      ref: z
        .string()
        .length(32)
        .regex(/^[A-Za-z0-9]+$/)
        .optional(),
    }),
  );

  useEffect(() => {
    const urlToken = safeSearchParams.ref;

    if (urlToken && urlToken !== storedToken) {
      dispatch(setCallbackRef(urlToken));
      sessionStorage.setItem(SESSION_STORAGE_KEY, urlToken);
      return;
    }

    if (!urlToken && !storedToken) {
      const persistedToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (persistedToken) dispatch(setCallbackRef(persistedToken));
    }
  }, [safeSearchParams]);
};
